import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllCanteens } from '../../../Screens/menu_planner/CanteenApi';
import { AuthActions } from '../../../store/auth-slice';
import { navRoute } from '../../../utils/navInfo';
import { queryKeys } from '../../../utils/queryKeys';

const getCanteenOptions = (response) => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
};

const CanteenSelectorMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useSelector((state) => state.auth);
  const selectedCanteenId = user?.canteen?.id ?? '';
  const queryKeysToInvalidate = useMemo(
    () =>
      new Set([
        queryKeys.getMealData,
        queryKeys.getAllScheduleData,
        queryKeys.getAllMealPortionsData,
        queryKeys.getAllPortionSizePrice,
        'get-allergies-data',
      ]),
    []
  );

  const { data, error, isLoading } = useQuery(
    queryKeys.getAllCanteens,
    getAllCanteens,
    {
      enabled: user?.roles?.includes('SuperAdmin'),
      refetchOnWindowFocus: false,
    }
  );

  const canteens = useMemo(() => getCanteenOptions(data), [data]);

  useEffect(() => {
    if (!selectedCanteenId || isLoading || error) {
      return;
    }

    const hasSelectedCanteen = canteens.some(
      (canteen) => `${canteen.id}` === `${selectedCanteenId}`
    );

    if (!hasSelectedCanteen) {
      dispatch(AuthActions.clearTargetCanteen());
    }
  }, [canteens, dispatch, error, isLoading, selectedCanteenId]);

  const handleCanteenChange = (event) => {
    const nextCanteenId = event.target.value;

    if (`${nextCanteenId}` === `${selectedCanteenId}`) {
      return;
    }

    const selectedCanteen = canteens.find(
      (canteen) => `${canteen.id}` === `${nextCanteenId}`
    );

    if (!selectedCanteen) {
      return;
    }

    dispatch(AuthActions.updateTargetCanteen(selectedCanteen));
    queryClient.invalidateQueries({
      predicate: (query) => {
        const rootQueryKey = Array.isArray(query.queryKey)
          ? query.queryKey[0]
          : query.queryKey;

        return queryKeysToInvalidate.has(rootQueryKey);
      },
    });

    if (location.pathname !== navRoute.navigateToFoodBox) {
      navigate(navRoute.navigateToFoodBox, { replace: true });
    }
  };

  return (
    <FormControl fullWidth size='small' error={Boolean(error)}>
      <InputLabel id='canteen-selector-label'>{t('Canteen')}</InputLabel>
      <Select
        labelId='canteen-selector-label'
        id='canteen-selector'
        value={selectedCanteenId}
        label={t('Canteen')}
        onChange={handleCanteenChange}
        disabled={isLoading || canteens.length === 0}
        sx={{
          backgroundColor: '#FFFFFF',
        }}
      >
        <MenuItem value='' disabled>
          {isLoading ? t('Loading canteens...') : t('Select canteen')}
        </MenuItem>
        {canteens.map((canteen) => (
          <MenuItem value={canteen.id} key={canteen.id}>
            {canteen.name}
          </MenuItem>
        ))}
      </Select>
      {error ? (
        <FormHelperText>
          {error?.response?.data?.message ?? t('Unable to load canteens')}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default CanteenSelectorMenu;
